"use client"

import { useState } from "react"
import { useLocale, useTranslations } from "next-intl"
import { usePathname, useRouter } from "@/i18n/navigation"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Globe } from "lucide-react"

const languages = [
  { value: "en-EU", label: "English", region: "EU" },
  { value: "de-AT", label: "Deutsch", region: "Österreich" },
]

export function FooterLanguageSwitcher() {
  const [open, setOpen] = useState(false)
  const locale = useLocale()
  const router = useRouter()
  const pathname = usePathname()
  const t = useTranslations("language")

  const currentLanguage = languages.find(lang => lang.value === locale) || languages[0]

  const handleLanguageChange = (newLocale: string) => {
    router.replace(pathname, { locale: newLocale })
    setOpen(false)
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 text-background/60 hover:text-background text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-foreground rounded-sm"
        aria-label={t("changeLanguage")}
      >
        <Globe className="h-4 w-4" aria-hidden="true" />
        <span>
          {currentLanguage.label} ({currentLanguage.region})
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("selectLanguage")}</DialogTitle>
            <DialogDescription>
              {t("selectDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-4">
            {languages.map((lang) => (
              <Button
                key={lang.value}
                variant={locale === lang.value ? "default" : "outline"}
                className="w-full justify-start"
                onClick={() => handleLanguageChange(lang.value)}
              >
                <span className="font-medium">{lang.label}</span>
                <span className="ml-2 text-sm opacity-75">({lang.region})</span>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
